import { inject, Injectable } from '@angular/core';
import { CreateTeamParams, Team, TeamMember, TeamRole, UpdateTeamParams } from '../models/team.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * Teams (`team.manage`) — purely organizational grouping (design doc §3.1/
 * §3.2), never a sync or visibility boundary. Membership drives report
 * grouping and default-tag stamping only.
 */
@Injectable({
  providedIn: 'root',
})
export class TeamService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  async getAll(): Promise<Team[]> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM teams WHERE deleted_at IS NULL ORDER BY name');
    return rows as Team[];
  }

  createQuery(): QuerySignal<Team[]> {
    return this.db.createQuery('teams', () => this.getAll());
  }

  async create(params: CreateTeamParams): Promise<Team> {
    const userId = this.requireUser('create');
    await this.db.init();
    const id = this.db.generateId();
    const now = this.clockSync.now();

    await this.db.run(
      `INSERT INTO teams (id, name, description, default_tag_id, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, params.name, params.description ?? '', params.default_tag_id ?? '', userId, now, userId, now],
    );

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve team after creation');
    return created;
  }

  async update(params: UpdateTeamParams): Promise<Team> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Team '${params.id}' not found`);

    const next: Team = {
      ...existing,
      name: params.name ?? existing.name,
      description: params.description ?? existing.description,
      default_tag_id: params.default_tag_id ?? existing.default_tag_id,
      is_active: params.is_active ?? existing.is_active,
    };

    await this.db.run(
      `UPDATE teams SET name = ?, description = ?, default_tag_id = ?, is_active = ?, updated_by = ?, updated_at = ?
       WHERE id = ?`,
      [next.name, next.description, next.default_tag_id, next.is_active, userId, this.clockSync.now(), params.id],
    );

    return { ...next, updated_by: userId };
  }

  private async getById(id: string): Promise<Team | null> {
    const rows = await this.db.exec('SELECT * FROM teams WHERE id = ? AND deleted_at IS NULL', [id]);
    return rows.length > 0 ? (rows[0] as Team) : null;
  }

  /** Active members of a team, most recently added last. */
  async getMembers(teamId: string): Promise<TeamMember[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM team_members WHERE team_id = ? AND deleted_at IS NULL ORDER BY created_at',
      [teamId],
    );
    return rows as TeamMember[];
  }

  createMembersQuery(teamId: string): QuerySignal<TeamMember[]> {
    return this.db.createQuery('team_members', () => this.getMembers(teamId));
  }

  /**
   * Adds a user to a team, or updates their role if already an active member —
   * uniqueness is enforced here (check-then-insert), never a DB constraint,
   * per backend/CLAUDE.md's synced-table rules.
   */
  async addMember(teamId: string, userId: string, role: TeamRole = 'member'): Promise<TeamMember> {
    const actorId = this.requireUser('add a member to');
    await this.db.init();

    const existingRows = (await this.db.exec(
      'SELECT * FROM team_members WHERE team_id = ? AND user_id = ? AND deleted_at IS NULL',
      [teamId, userId],
    )) as TeamMember[];

    if (existingRows.length > 0) {
      await this.setMemberRole(existingRows[0].id, role);
      return { ...existingRows[0], team_role: role };
    }

    const id = this.db.generateId();
    const now = this.clockSync.now();
    await this.db.run(
      'INSERT INTO team_members (id, team_id, user_id, team_role, created_by, created_at) VALUES (?, ?, ?, ?, ?, ?)',
      [id, teamId, userId, role, actorId, now],
    );
    return { id, team_id: teamId, user_id: userId, team_role: role, created_by: actorId, created_at: now, deleted_at: null };
  }

  async setMemberRole(memberId: string, role: TeamRole): Promise<void> {
    await this.db.init();
    await this.db.run('UPDATE team_members SET team_role = ? WHERE id = ?', [role, memberId]);
  }

  /** Soft-removes a member from a team. */
  async removeMember(memberId: string): Promise<void> {
    await this.db.init();
    await this.db.run('UPDATE team_members SET deleted_at = ? WHERE id = ?', [this.clockSync.now(), memberId]);
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error(`User must be authenticated to ${action} a team`);
    return userId;
  }
}
